import React, { useCallback, useContext, useEffect, useState } from 'react';

import { FormControl, FormControlLabel, Radio, RadioGroup, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import { ReactComponent as ChevronDownIcon } from '../../../../../assets/icons/v3/chevron-down.svg';
import { DEFAULT_DATA_ORIGIN } from '../../../../constants/constants';
import { PERMISSIONS_BY_ROUTE } from '../../../../constants/permissions';
import { CLICK_ACTION, SAVE_CHANGES, SCREEN_HOME, SECTION_ORGANIZATION } from '../../../../constants/tagManager';
import { CompanyContext } from '../../../../context/company/companyContext';
import usePermissions from '../../../../hooks/usePermissions/usePermissions';
import { IUserCompany, IUserHolding } from '../../../../interfaces/businessData';
import TagManagerService from '../../../../services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../translations/commons';
import BdsButton from '../../Buttons/BdsButton/BdsButton';
import BdsDivider from '../BdsDivider/BdsDivider';
import GenericSideBar from '../GenericSideBar/GenericSideBar';

import './CompanySelector.scss';

interface ICompanyHoldingDict {
  [companyId: string]: {
    company: IUserCompany;
    holding: IUserHolding;
  };
}

const CompanySelector = () => {
  const [openCompanyMenu, setOpenCompanyMenu] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [companyHoldingDict, setCompanyHoldingDict] = useState<ICompanyHoldingDict>();
  const { company, holdingList, setUserCompanyAndHolding, setUserDataOrigin } = useContext(CompanyContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { companyRole, hasAnyAccess } = usePermissions();
  const { hide } = useIntercom();

  const { t } = useTranslation();

  const handleCompanyMenu = useCallback(() => {
    if (!companyHoldingDict) return;
    const selectedCompanyHolding = companyHoldingDict[selectedCompany];
    const companyData = selectedCompanyHolding.company;
    const holdingData = selectedCompanyHolding.holding;
    const routeName = location.pathname;

    if (!hasAnyAccess(PERMISSIONS_BY_ROUTE[routeName], companyData)) {
      navigate(companyRole.defaultRoute);
    }
    hide();
    setUserCompanyAndHolding(companyData, holdingData);
    setUserDataOrigin(DEFAULT_DATA_ORIGIN);
    TagManagerService.pushEvent(CLICK_ACTION, SAVE_CHANGES, { ...SECTION_ORGANIZATION, ...SCREEN_HOME });
    handleCloseCompanyMenu();
  }, [
    navigate,
    companyRole.defaultRoute,
    setUserCompanyAndHolding,
    hasAnyAccess,
    setUserDataOrigin,
    hide,
    selectedCompany,
    companyHoldingDict
  ]);

  const handleOpenSideBarClick = () => {
    setOpenCompanyMenu(!openCompanyMenu);
  };

  const handleCloseCompanyMenu = () => {
    setOpenCompanyMenu(false);
  };

  const handleSelectCompany = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectedCompany(value);
  };

  useEffect(() => {
    const companies: ICompanyHoldingDict = {};

    holdingList.forEach((holding) => {
      holding.companies.forEach((company) => {
        companies[company.id] = {
          company: company,
          holding: holding
        };
      });
    });
    setCompanyHoldingDict(companies);
    if (company.id) {
      setSelectedCompany(company.id);
    }
  }, [holdingList, company.id]);

  return company.id != '' ? (
    <>
      <button className="company-selector cursor-pointer" data-experience="new" onClick={handleOpenSideBarClick}>
        <div className="company-selector-text">{company.name}</div>
        <ChevronDownIcon />
      </button>
      <GenericSideBar isOpen={openCompanyMenu} onClose={handleCloseCompanyMenu} position="left">
        <div className="company-sidebar">
          <div className="company-sidebar-title">{t(TRANSLATIONS_GLOBAL.header.companySelector.title)}</div>
          <div className="company-sidebar-container">
            <FormControl variant="standard" className="w-full">
              <RadioGroup
                row
                value={selectedCompany}
                className="flex flex-col"
                name="radio-buttons-group"
                role="company-selector"
                onChange={handleSelectCompany}
              >
                <div className="company-sidebar-holding-container">
                  {holdingList.map((holdingData) => (
                    <div key={holdingData.id}>
                      <div key={holdingData.name} className="company-sidebar-holding">
                        {holdingData.name}
                      </div>

                      {holdingData.companies.map((companyData) => (
                        <React.Fragment key={companyData.id}>
                          <FormControlLabel
                            value={companyData.id}
                            className="company-sidebar-selector"
                            control={<Radio className="company-sidebar-circle" />}
                            label={companyData.name}
                            labelPlacement="start"
                          />
                          <BdsDivider color="high" size="s" />
                        </React.Fragment>
                      ))}
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </FormControl>
          </div>
          <BdsButton action={handleCompanyMenu} disabled={!selectedCompany} variant="inverse" size="s">
            {t(TRANSLATIONS_GLOBAL.header.companySelector.button)}
          </BdsButton>
        </div>
      </GenericSideBar>
    </>
  ) : (
    <div className="company-selector">
      <Skeleton animation="wave" variant="text" width={300}></Skeleton>
    </div>
  );
};

export default CompanySelector;
